import {onRedirect, pageData} from "@/helpers/CypressHelper";
import Localizer from "@/localization/Localizer";
import RentaEasyConstants from "@/helpers/RentaEasyConstants";
import DropdownHelper from "@/helpers/CypressDropdownHelper";
import {SortDirection} from "@renta-apps/athenaeum-toolkit";
import DeviceGridHelper from "@/helpers/DeviceGridHelper";

describe('Desktop - Admin user construction site details', () => {
    before(() => {
    });

    beforeEach(() => {
        cy.useSharedAdminUserSession();

        pageData().common.interceptFeatureFlags([
            {flagName: RentaEasyConstants.featureFlagConstructionSiteNewEquipmentPage, enabled: true},
            {flagName: RentaEasyConstants.featureFlagRentalReturnRequest, enabled: true},
            {flagName: RentaEasyConstants.featureFlagServicesView, enabled: true},
        ]);

        cy.visit('frontpage');
    });

    it('Should display page with construction site equipment', () => {
        cy.intercept('POST', '/api/ConstructionSites/ProductsWithTelematics', { fixture: 'productsWithTelematics.json' });

        onRedirect(() => cy.visit(`${Localizer.pageRoutesConstructionSiteDetails}?id=20c46273-6c09-4c7d-98bf-300071dfc1e2`));

        pageData().constructionSite.equipment.tab().click();

        const {new: equipment} = pageData().constructionSite.equipment;

        equipment.grid().should('exist');

        equipment.deviceName(0).should('contain.text', 'Kynsiliitin 75 MM');
        equipment.deviceName(0).children('div').eq(1).children('span').eq(0).should('have.attr', 'class').and('contain', 'disabled');
        equipment.rentaId(0).should('contain.text', '100025');
        // returned device
        equipment.returnButton(0).should('be.disabled');
        equipment.serviceRequestButton(0).should('be.disabled');
        equipment.subscribeButton(0).should('be.disabled');

        equipment.deviceName(1).should('contain.text', 'Dieselkuukulkija 25,75M Manitou 280 TJ');
        equipment.deviceName(1).children('div').eq(1).children('span').eq(0).should('have.attr', 'class').and('contain', 'link');
        equipment.rentaId(1).should('contain.text', '96000');
        // supports alerts, return requested
        equipment.deviceName(1)
            .find('div[data-cy="tooltipWrapper"]')
            .find('[data-cy="truck-icon"]')
            .should('exist');
        equipment.returnButton(1).should('be.disabled');
        equipment.serviceRequestButton(1).should('be.enabled');
        equipment.subscribeButton(1).should('be.enabled');

        equipment.deviceName(2).should('contain.text', 'Dieselkuukulkija 16,0 M Jlg 520AJ');
        equipment.rentaId(2).should('contain.text', '90631');
        // does not support alerts
        equipment.returnButton(2).should('be.enabled');
        equipment.serviceRequestButton(2).should('be.enabled');
        equipment.subscribeButton(2).should('be.disabled');

        equipment.pagination().should('be.visible');
    });

    it('Should display telematics information correctly', () => {
        cy.intercept('POST', '/api/ConstructionSites/ProductsWithTelematics', { fixture: 'productsWithTelematics.json' });

        onRedirect(() => cy.visit(`${Localizer.pageRoutesConstructionSiteDetails}?id=20c46273-6c09-4c7d-98bf-300071dfc1e2`));

        pageData().constructionSite.equipment.tab().click();

        const {new: equipment} = pageData().constructionSite.equipment;

        equipment.grid().should('exist');

        // returned device
        equipment.telematics(0).should('not.exist');

        // non-tracking device
        equipment.telematics(2).should('not.exist');

        // all the telematics
        equipment.telematics(1).should('be.visible');

        equipment.telematicsAlerts(1)
            .should('contain', 20);

        equipment.telematicsIdle(1)
            .should('contain', '13d');

        equipment.telematicsFluid(1)
            .should('contain', '74%');

        // Check if there are three battery level rectangles.
        equipment.telematicsBattery(1)
            .find('div[class*="rectangleLevel"]')
            .should('have.length', 3);

        // Check if one of the three rectangles is filled.
        equipment.telematicsBattery(1)
            .find('div[class*="filled"]')
            .should('have.length', 1);
    });

    it('should change sort column correctly', () => {
        cy.intercept('POST', '/api/ConstructionSites/ProductsWithTelematics', req => {
            if (req.body.sortColumnName === 'BatteryLevel') {
                req.alias = 'postRequestChanged';
            } else {
                req.alias = 'postRequest';
            }
            req.reply({fixture: 'productsWithTelematics.json'});
        });

        onRedirect(() => cy.visit(`${Localizer.pageRoutesConstructionSiteDetails}?id=20c46273-6c09-4c7d-98bf-300071dfc1e2`));

        pageData().constructionSite.equipment.tab().click();

        const {new: equipment} = pageData().constructionSite.equipment;

        cy.wait('@postRequest', {timeout: 20_000}).then(({request}) => {
            // Assert the initial request body
            expect(request.body).to.have.property('sortColumnName', 'CategoryName');

            // Select the Battery option from the dropdown
            DropdownHelper.selectDropdownOption(equipment.sortBy(), 7);

            // Wait for the second intercepted POST request to the API endpoint
            cy.wait('@postRequestChanged').then(({request}) => {
                // Assert that the request body contains the changed value
                expect(request.body).to.have.property('sortColumnName', 'BatteryLevel');

                cy.url().should('include', `${Localizer.pageRoutesConstructionSiteDetails}`)
                    .should('include', 'sortBy=BatteryLevel&sortOrder=Asc');
            });
        });
    });

    it('should change sort order correctly', () => {
        cy.intercept('POST', '/api/ConstructionSites/ProductsWithTelematics', req => {
            if (req.body.sortDirection === SortDirection.Desc) {
                req.alias = 'postRequestChanged';
            } else {
                req.alias = 'postRequest';
            }
            req.reply({fixture: 'productsWithTelematics.json'});
        });

        onRedirect(() => cy.visit(`${Localizer.pageRoutesConstructionSiteDetails}?id=20c46273-6c09-4c7d-98bf-300071dfc1e2`));

        pageData().constructionSite.equipment.tab().click();

        const {new: equipment} = pageData().constructionSite.equipment;

        cy.wait('@postRequest', {timeout: 20_000}).then(({request}) => {
            // Assert the initial request body
            expect(request.body).to.have.property('sortDirection', SortDirection.Asc);

            // Select the Battery option from the dropdown
            DropdownHelper.selectDropdownOption(equipment.sortOrder(), 1);

            // Wait for the second intercepted POST request to the API endpoint
            cy.wait('@postRequestChanged').then(({request}) => {
                // Assert that the request body contains the changed value
                expect(request.body).to.have.property('sortDirection', SortDirection.Desc);

                cy.url().should('include', `${Localizer.pageRoutesConstructionSiteDetails}`)
                    .should('include', 'sortBy=CategoryName&sortOrder=Desc');
            });
        });
    });

    it('should set device names with the value obtain from URL', () => {
        cy.intercept('POST', '/api/ConstructionSites/ProductsWithTelematics', req => {
            if (req.body.deviceNames?.[0] === 'volvo') {
                req.alias = 'postRequestFiltered';
            } else {
                req.alias = 'postRequest';
            }
            req.reply({fixture: 'productsWithTelematics.json'});
        });

        onRedirect(() => cy.visit(`${Localizer.pageRoutesConstructionSiteDetails}?id=20c46273-6c09-4c7d-98bf-300071dfc1e2&selectedTab=kalusto&deviceNames=["test"]&statuses=[]`));

        const {new: equipment} = pageData().constructionSite.equipment;
        cy.wait('@postRequest').then(() => {
            equipment.filters.deviceNames()
                .should('have.attr', 'placeholder', Localizer.get(Localizer.componentDropdownMultipleSelectedLanguageItemName, 1));

            DeviceGridHelper.checkTags(["test"]);

            DeviceGridHelper.deleteTag(0);

            equipment.filters.deviceNames().type('volvo');

            equipment.filters.searchButton().click();

            cy.wait('@postRequestFiltered').then(({request}) => {
                // Assert that the request body contains the changed value
                expect(request.body).to.have.property('deviceNames');
                expect(request.body.deviceNames).to.have.length(1);
                expect(request.body.deviceNames[0] === 'volvo');
            });
        });
    });

    it('should set product group with the value obtain from URL', () => {
        cy.intercept('POST', '/api/ConstructionSites/ProductsWithTelematics', req => {
            if (req.body.productGroups?.[0]?.name === 'lift') {
                req.alias = 'postRequestFiltered';
            } else {
                req.alias = 'postRequest';
            }
            req.reply({fixture: 'productsWithTelematics.json'});
        });
        cy.intercept('GET', '/api/SearchLogs/GetMostPopular*', { fixture: 'getMostPopularSearches.json' });

        onRedirect(() => cy.visit(`${Localizer.pageRoutesConstructionSiteDetails}?id=20c46273-6c09-4c7d-98bf-300071dfc1e2&selectedTab=kalusto&productGroups=[{"id":"66668","name":"Hilti%20DX%206%20(66668)"}]&statuses=[]`));

        const {new: equipment} = pageData().constructionSite.equipment;
        cy.wait('@postRequest').then(() => {
            equipment.filters.productGroups()
                .should('have.attr', 'placeholder', Localizer.get(Localizer.componentDropdownMultipleSelectedLanguageItemName, 1));

            DeviceGridHelper.checkTags(["Hilti DX 6 (66668)"]);

            DeviceGridHelper.deleteTag(0);

            // popular searches
            equipment.filters.productGroups().focus();
            equipment.filters.productGroupsContainer().children('div').eq(1).children().should('have.length', 3);
            equipment.filters.productGroupsContainer().children('div').eq(1).children('div').eq(0).should('contain.text', 'Product group 1 (11111)');
            
            equipment.filters.productGroups().type('lift');

            equipment.filters.searchButton().click();

            // user's recent search
            equipment.filters.productGroups().focus();
            equipment.filters.productGroupsContainer().children('div').eq(1).children().should('have.length', 1);
            equipment.filters.productGroupsContainer().children('div').eq(1).children('div').eq(0).should('contain.text', 'lift');

            cy.wait('@postRequestFiltered').then(({request}) => {
                // Assert that the request body contains the changed value
                expect(request.body).to.have.property('productGroups');
                expect(request.body.productGroups).to.have.length(1);
                expect(request.body.productGroups?.[0].name === 'lift');
            });
        });
    });

    it('should set status with the value obtain from URL', () => {
        cy.intercept('POST', '/api/ConstructionSites/ProductsWithTelematics', req => {
            if (req.body.statuses?.[0]?.id === 'ReturnRequested') {
                req.alias = 'postRequestFiltered';
            } else {
                req.alias = 'postRequest';
            }
            req.reply({fixture: 'productsWithTelematics.json'});
        });

        onRedirect(() => cy.visit(`${Localizer.pageRoutesConstructionSiteDetails}?id=20c46273-6c09-4c7d-98bf-300071dfc1e2&selectedTab=kalusto&statuses=[{"id":"Returned","name":"Returned"}]`));

        const {new: equipment} = pageData().constructionSite.equipment;
        cy.wait('@postRequest').then(() => {
            equipment.filters.toggleAdditionalFiltersButton().click();

            equipment.filters.statuses().get('[data-cy="dropdownInput"]')
                .should('contain.text', Localizer.get(Localizer.componentDropdownMultipleSelectedLanguageItemName, 1));

            DeviceGridHelper.checkTags(["Returned"]);

            DeviceGridHelper.deleteTag(0);

            DropdownHelper.selectDropdownOption(equipment.filters.statuses(), 2);

            cy.wait('@postRequestFiltered').then(({request}) => {
                // Assert that the request body contains the changed value
                expect(request.body).to.have.property('statuses');
                expect(request.body.statuses).to.have.length(1);
                expect(request.body.statuses?.[0]?.id === 'ReturnRequested');
            });
        });
    });

    it('should have default status value', () => {
        cy.intercept('POST', '/api/ConstructionSites/ProductsWithTelematics', { fixture: 'productsWithTelematics.json' });

        onRedirect(() => cy.visit(`${Localizer.pageRoutesConstructionSiteDetails}?id=20c46273-6c09-4c7d-98bf-300071dfc1e2&selectedTab=kalusto`));

        const {new: equipment} = pageData().constructionSite.equipment;
        equipment.filters.toggleAdditionalFiltersButton().click();

        equipment.filters.statuses().get('[data-cy="dropdownInput"]')
            .should('contain.text', Localizer.get(Localizer.componentDropdownMultipleSelectedLanguageItemName, 1));

        DeviceGridHelper.checkTags([Localizer.constructionSiteDetailsEquipmentTabStatusInRent]);
    });

    it('should expand a row and display additional data', () => {
        cy.intercept('POST', '/api/ConstructionSites/ProductsWithTelematics', { fixture: 'productsWithTelematics.json' });

        cy.intercept('POST', '/api/ConstructionSites/ProductWithTelematicsDetails', { fixture: 'productWithTelematicsDetails.json' });

        onRedirect(() => cy.visit(`${Localizer.pageRoutesConstructionSiteDetails}?id=20c46273-6c09-4c7d-98bf-300071dfc1e2`));

        pageData().constructionSite.equipment.tab().click();

        const {new: equipment} = pageData().constructionSite.equipment;
        const startDate = new Date('2024-08-08T00:00:00Z');
        const returnRequestedAt1 = new Date('2024-08-18T00:00:00Z');
        const returnRequestedPickUpTime1 = new Date('2024-08-18T00:00:00Z');
        const returnRequestedAt2 = new Date('2024-08-28T00:00:00Z');
        const returnRequestedPickUpTime2 = new Date('2024-08-28T00:00:00Z');
        const contractNumber1 = '24-482858';
        const contractNumber2 = '24-354602';

        equipment.grid().should('exist');
        equipment.accordionButton(0).click();
        equipment.productGroup().should('contain.text', 'Konenostaja 500');
        equipment.category().should('contain.text', 'Konenostimet');
        equipment.rentalStartDate().should('contain.text', startDate.toLocaleDateString());
        equipment.rentalEndDate().should('contain.text', '-');
        equipment.orderBy().should('contain.text', 'Pär Lundeflo');
        equipment.totalCost().should('contain.text', '13.54');
        equipment.contractNumber().should('contain.text', '24-354602');
        equipment.amount().should('contain.text', '3');

        equipment.returnRequestedAt(0).should('contain.text', returnRequestedAt1.toLocaleDateString());
        equipment.returnAmount(0).should('contain.text', '2');
        equipment.returnPickUpTime(0).should('contain.text',  returnRequestedPickUpTime1.toLocaleDateString());
        equipment.returnRequestedBy(0).should('contain.text', 'Thomas Sööder');

        equipment.returnRequestedAt(1).should('contain.text', returnRequestedAt2.toLocaleDateString());
        equipment.returnAmount(1).should('contain.text', '1');
        equipment.returnPickUpTime(1).should('contain.text',  returnRequestedPickUpTime2.toLocaleDateString());
        equipment.returnRequestedBy(1).should('contain.text', 'Johan Davidsson');

        equipment.accordionTitleAreaButton(`${Localizer.equipmentGridContract}${contractNumber1}`).click();
        equipment.amountRentedInitially(0).should('contain.text', '18');
        equipment.totalReturned(0).should('contain.text', '7');
        equipment.amountOnRent(0).should('contain.text', '11');

        equipment.accordionTitleAreaButton(`${Localizer.equipmentGridContract}${contractNumber2}`).click();
        equipment.amountRentedInitially(1).should('contain.text', '3');
        equipment.totalReturned(1).should('contain.text', '0');
        equipment.amountOnRent(1).should('contain.text', '3');
    });

    it('should show and hide additional filters on button click', () => {
        cy.intercept('POST', '/api/ConstructionSites/ProductsWithTelematics', { fixture: 'productsWithTelematics.json' });

        onRedirect(() => cy.visit(`${Localizer.pageRoutesConstructionSiteDetails}?id=20c46273-6c09-4c7d-98bf-300071dfc1e2`));

        pageData().constructionSite.equipment.tab().click();

        const {new: equipment} = pageData().constructionSite.equipment;
        equipment.filters.additionalFiltersContainer().should('not.exist');
        equipment.filters.toggleAdditionalFiltersButton().click();
        equipment.filters.additionalFiltersContainer().should('be.visible');
        equipment.filters.toggleAdditionalFiltersButton().click();
        equipment.filters.additionalFiltersContainer().should('not.exist');
    });
});